import React, { useState, useEffect, useCallback } from "react";    // Added useCallback import by Gauri on 06 Nov 2025
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import { Box, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AxiosInstance1 from "app/hooks/AxiosInstance/AxiosInstance1";

const TimelineHistoryDrawer = ({ open, onClose, initiativesID }) => {
  // Added by Gauri to add pagination on 06 Nov 2025
  const [historyData, setHistoryData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(5);
  const [loading, setLoading] = useState(false);

  // Added by Gauri to fetch timeline history data with pagination on 06 Nov 2025
  const fetchHistoryData = useCallback(async () => {
    if (!initiativesID) {
      setHistoryData([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    try {
      const response = await AxiosInstance1.get(
        `/api/InitiativeDetail/GetInitiativeTimelineHistory?IdeaId=${initiativesID}&PageNumber=${currentPage}&PageSize=${pageSize}`
      );

      const data = response?.data;
      const timelineData = data?.data?.listTATHistory || [];
      const totalRecords = data?.paginationEntity?.[0]?.totalRecords || 0;

      setHistoryData(timelineData);
      setTotalCount(totalRecords);
    } catch (error) {
      console.error("Error fetching timeline history data:", error);
      setHistoryData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [initiativesID, currentPage, pageSize]);

  // Reset to page 1 when drawer opens
  useEffect(() => {
    if (open) {
      setCurrentPage(1);
    }
  }, [open]);

  // Fetch data when drawer is open and pagination changes
  useEffect(() => {
    if (open && initiativesID) {
      fetchHistoryData();
    }
  }, [open, initiativesID, fetchHistoryData]);

  // Added by Gauri to handle pagination on 06 Nov 2025
  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const handleNextPage = () => {
    const maxPages = Math.ceil(totalCount / pageSize);
    setCurrentPage((prevPage) => {
      if (prevPage >= maxPages) {
        return prevPage;
      }
      return prevPage + 1;
    });
  };

  // Added by Gauri to format dates as DD/MM/YYYY on 06 Nov 2025
  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    
    try {
      const dateObj = new Date(dateValue);
      if (!isNaN(dateObj.getTime())) {
        // Format as DD/MM/YYYY
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (error) {
      console.error("Error formatting date:", error);
    }
    
    return "";
  };
  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      sx={{
        "& .MuiDrawer-paper": {
          width: "70%",
          maxWidth: "100%"
        }
      }}
    >
      <div className="offcanvas-body">
        <div id="initSnooze" className="inithistDetails">
          {/* Title Section */}
          <div className="graybg container-fluid py-2 mb-2" style={{ backgroundColor: "#f0f0f0" }}>
            <div className="row">
              <div className="col-sm-10">
                <h5 className="offcanvasTitle">Timeline History</h5>
              </div>
              <div className="col-sm-2 text-end">
                <IconButton onClick={onClose}>
                  <Tooltip title="Close">
                    <CloseIcon />
                  </Tooltip>
                </IconButton>
              </div>
            </div>
          </div>

          <div className="historyHeight px-3">
            <div className="table-responsive offTable_wrapper mt-2">
              <table
                className="table table-striped table-bordered table-hover"
                aria-label="Timeline History Table"
              >
                <thead>
                  <tr>
                    <th className="text-center">Comments</th>
                    <th>Stage</th>
                    <th>Planned Start Date</th>
                    <th>Planned End Date</th>
                    <th>Planned Duration</th>
                    <th>Modified By</th>
                    <th>Modified Date</th>
                  </tr>
                </thead>
                <tbody>
                  {historyData && historyData.length > 0 ? (
                    historyData.map((item, index) => (
                      <tr key={index}>
                        {/* Modified all fields by Gauri on 06 Nov 2025 */}
                        <td className="text-center">{item?.comments || "N/A"}</td>
                        <td>{item?.requestStage || "N/A"}</td>
                        {/* Added by Gauri to format dates as DD/MM/YYYY on 06 Nov 2025 */}
                        <td>{formatDate(item?.stagePlannedStartDate)}</td>
                        <td>{formatDate(item?.stagePlannedEndDate)}</td>
                        <td>{item?.plannedDuration || "N/A"}</td>
                        <td>{item?.modifiedBy || "N/A"}</td>
                        <td>{formatDate(item?.modifiedDate)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center" }}>
                        There are no items to show in this view.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Added by Gauri to add pagination controls on 06 Nov 2025 */}
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px", marginBottom: "20px" }}>
            <IconButton onClick={handlePreviousPage} disabled={currentPage === 1 || totalCount === 0 || loading}>
              <ArrowBackIcon />
            </IconButton>
            <Typography sx={{ margin: "0 10px", alignSelf: "center" }}>
              Page {currentPage}
            </Typography>
            <IconButton
              onClick={handleNextPage}
              disabled={totalCount === 0 || currentPage >= Math.ceil(totalCount / pageSize) || loading}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        </div>
      </div>
    </Drawer>
  );
};

export default TimelineHistoryDrawer;
